import React, { useState } from "react";
import AvailabilityCalendar from "./AvailabilityCalendar";
import StripeCheckout from "../payment/StripeCheckout";

interface BookingInterfaceProps {
  sessionType?: string | null;
  onBookingComplete?: (bookingId: string) => void;
  clientData?: any;
  defaultSessionType?: string;
}

type BookingStep = "scheduling" | "payment" | "confirmation";

const BookingInterface: React.FC<BookingInterfaceProps> = ({
  sessionType,
  onBookingComplete,
  clientData,
  defaultSessionType = "individual",
}) => {
  const [currentStep, setCurrentStep] = useState<BookingStep>("scheduling");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState<string | null>(null);

  const handleTimeSlotSelect = (timeSlot: string) => {
    setSelectedTimeSlot(timeSlot);
    setBookingError(null);
  };

  const handleProceedToPayment = () => {
    if (!selectedTimeSlot) {
      setBookingError("Please select a time slot before proceeding");
      return;
    }
    setCurrentStep("payment");
  };

  const handleBackToScheduling = () => {
    setCurrentStep("scheduling");
    setBookingError(null);
  };

  const handlePaymentSuccess = (sessionId: string) => {
    setBookingSuccess(`Payment successful! Session ID: ${sessionId}`);
    setCurrentStep("confirmation");
    onBookingComplete?.(sessionId);
  };

  const handlePaymentError = (error: string) => {
    setBookingError(error);
  };

  const formatTimeSlot = (timeSlot: string): string => {
    return new Date(timeSlot).toLocaleString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZoneName: "short",
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Progress indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-center space-x-4">
          <div
            className={`flex items-center ${currentStep === "scheduling" ? "text-brand-green-600" : currentStep === "payment" || currentStep === "confirmation" ? "text-brand-green-800" : "text-brand-stone-400"}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${currentStep === "scheduling" ? "bg-brand-green-600 text-white" : currentStep === "payment" || currentStep === "confirmation" ? "bg-brand-green-800 text-white" : "bg-brand-stone-200"}`}
            >
              1
            </div>
            <span className="ml-2 font-medium">Select Time</span>
          </div>

          <div
            className={`h-1 w-16 ${currentStep === "payment" || currentStep === "confirmation" ? "bg-brand-green-600" : "bg-brand-stone-200"}`}
          ></div>

          <div
            className={`flex items-center ${currentStep === "payment" ? "text-brand-green-600" : currentStep === "confirmation" ? "text-brand-green-800" : "text-brand-stone-400"}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${currentStep === "payment" ? "bg-brand-green-600 text-white" : currentStep === "confirmation" ? "bg-brand-green-800 text-white" : "bg-brand-stone-200"}`}
            >
              2
            </div>
            <span className="ml-2 font-medium">Payment</span>
          </div>

          <div
            className={`h-1 w-16 ${currentStep === "confirmation" ? "bg-brand-green-600" : "bg-brand-stone-200"}`}
          ></div>

          <div
            className={`flex items-center ${currentStep === "confirmation" ? "text-brand-green-600" : "text-brand-stone-400"}`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${currentStep === "confirmation" ? "bg-brand-green-600 text-white" : "bg-brand-stone-200"}`}
            >
              3
            </div>
            <span className="ml-2 font-medium">Confirmation</span>
          </div>
        </div>
      </div>

      {/* Error display */}
      {bookingError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700 font-medium">Error</p>
          <p className="text-red-600 text-sm">{bookingError}</p>
        </div>
      )}

      {/* Success display */}
      {bookingSuccess && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-700 font-medium">Success!</p>
          <p className="text-green-600 text-sm">{bookingSuccess}</p>
        </div>
      )}

      {/* Step content */}
      {currentStep === "scheduling" && (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-brand-green-800 mb-2">
              Book Your Session with Hailey
            </h2>
            <p className="text-brand-stone-600">
              Select a convenient time for your consultation session
            </p>
          </div>

          <AvailabilityCalendar
            onSlotSelect={handleTimeSlotSelect}
            selectedSlot={selectedTimeSlot || undefined}
          />

          {selectedTimeSlot && (
            <div className="text-center">
              <div className="mb-4 p-4 bg-brand-green-50 rounded-lg border border-brand-green-200 inline-block">
                <p className="text-sm font-medium text-brand-green-800 mb-1">
                  Selected time:
                </p>
                <p className="text-brand-green-700 font-semibold">
                  {formatTimeSlot(selectedTimeSlot)}
                </p>
              </div>

              <div>
                <button
                  onClick={handleProceedToPayment}
                  className="px-8 py-3 bg-brand-green-600 text-white font-medium rounded-lg hover:bg-brand-green-700 transition-colors"
                >
                  Proceed to Payment
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {currentStep === "payment" && (
        <div className="space-y-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-brand-green-800 mb-2">
              Complete Your Booking
            </h2>
            <p className="text-brand-stone-600">
              Select your session type and complete payment
            </p>
          </div>

          {selectedTimeSlot && (
            <div className="text-center mb-4">
              <div className="p-3 bg-brand-green-50 rounded-lg border border-brand-green-200 inline-block">
                <p className="text-sm font-medium text-brand-green-800 mb-1">
                  Scheduled for:
                </p>
                <p className="text-brand-green-700 font-semibold">
                  {formatTimeSlot(selectedTimeSlot)}
                </p>
              </div>
            </div>
          )}

          <StripeCheckout
            selectedTimeSlot={selectedTimeSlot || undefined}
            preselectedSessionType={sessionType || defaultSessionType}
            onSuccess={handlePaymentSuccess}
            onError={handlePaymentError}
            clientData={clientData}
          />

          <div className="text-center">
            <button
              onClick={handleBackToScheduling}
              className="px-6 py-2 border border-brand-stone-300 text-brand-stone-700 rounded-lg hover:bg-brand-stone-50 transition-colors"
            >
              ← Back to Time Selection
            </button>
          </div>
        </div>
      )}

      {currentStep === "confirmation" && (
        <div className="text-center space-y-6">
          <div className="w-16 h-16 bg-brand-green-100 rounded-full flex items-center justify-center mx-auto">
            <svg
              className="w-8 h-8 text-brand-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <div>
            <h2 className="text-2xl font-bold text-brand-green-800 mb-2">
              Booking Confirmed!
            </h2>
            <p className="text-brand-stone-600 mb-4">
              Your session has been successfully booked and paid for.
            </p>

            {selectedTimeSlot && (
              <div className="p-4 bg-brand-green-50 rounded-lg border border-brand-green-200 inline-block mb-4">
                <p className="text-sm font-medium text-brand-green-800 mb-1">
                  Your session is scheduled for:
                </p>
                <p className="text-brand-green-700 font-semibold">
                  {formatTimeSlot(selectedTimeSlot)}
                </p>
              </div>
            )}
          </div>

          <div className="text-sm text-brand-stone-600 space-y-2 max-w-md mx-auto">
            <p>✉️ A confirmation email has been sent to you</p>
            <p>📅 You'll receive a calendar invite shortly</p>
            <p>
              💬 You can access your client portal anytime to communicate with
              Hailey
            </p>
            <p>⏰ Please arrive 5 minutes before your scheduled time</p>
          </div>

          <div className="space-x-4">
            <button
              onClick={() => (window.location.href = `/client/portal`)}
              className="px-6 py-2 bg-brand-green-600 text-white font-medium rounded-lg hover:bg-brand-green-700 transition-colors"
            >
              Go to Client Portal
            </button>

            <button
              onClick={() => {
                setCurrentStep("scheduling");
                setSelectedTimeSlot(null);
                setBookingError(null);
                setBookingSuccess(null);
              }}
              className="px-6 py-2 border border-brand-stone-300 text-brand-stone-700 rounded-lg hover:bg-brand-stone-50 transition-colors"
            >
              Book Another Session
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingInterface;
